/**
 * Hablantes (SDD §7.2): renombrar ("Participante 2" → "Carla") y fusionar
 * cuando la diarización partió a una persona en dos. Los cambios aplican a
 * toda la grabación; las notas ya generadas conservan las etiquetas estables y
 * muestran el nombre nuevo.
 */
import { useEffect, useState } from 'react'
import { speakerDisplayName, type RecordingDetail, type Speaker } from '@eco/shared'
import { useApp, useToast, errorText } from './AppContext'
import { Modal } from './ui'
import { formatLength } from '../lib/format'
import { speakerNameHints } from '../lib/speakerHints'
import { talkTime } from '../lib/transcript'

export function SpeakersDialog({
  open,
  onClose,
  detail,
  focus,
  onChanged
}: {
  open: boolean
  onClose: () => void
  detail: RecordingDetail
  focus: Speaker | null
  onChanged: () => Promise<void>
}) {
  const { api } = useApp()
  const toast = useToast()
  const [names, setNames] = useState<Record<number, string>>({})
  const [mergeFrom, setMergeFrom] = useState<number | ''>('')
  const [mergeInto, setMergeInto] = useState<number | ''>('')
  const [busy, setBusy] = useState(false)
  const talk = new Map(talkTime(detail.segments).map((t) => [t.speaker, t]))
  const hints = new Map(speakerNameHints(detail.segments).map((h) => [h.speaker, h]))

  useEffect(() => {
    if (open) {
      setNames(Object.fromEntries(detail.speakers.map((s) => [s.id, s.nombre ?? ''])))
      setMergeFrom('')
      setMergeInto('')
    }
    // Solo al abrir: el polling trae `speakers` nuevos cada pocos segundos
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  async function save() {
    setBusy(true)
    try {
      for (const s of detail.speakers) {
        const next = (names[s.id] ?? '').trim()
        if (next !== (s.nombre ?? ''))
          await api.renameSpeaker(detail.recording.id, s.id, next || null)
      }
      if (mergeFrom !== '' && mergeInto !== '' && mergeFrom !== mergeInto)
        await api.mergeSpeakers(detail.recording.id, [mergeFrom], mergeInto)
      await onChanged()
      toast('Hablantes actualizados')
      onClose()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  const label = (s: Speaker) => speakerDisplayName(s.etiqueta, s.nombre)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Hablantes"
      footer={
        <>
          <button className="btn ghost" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button className="btn primary" onClick={() => void save()} disabled={busy}>
            Guardar
          </button>
        </>
      }
    >
      <p className="muted" style={{ fontSize: 13 }}>
        Ponle nombre a cada voz. Se aplica a toda la transcripción, las notas y lo que exportes.
      </p>
      {detail.speakers.map((s) => {
        const t = talk.get(label(s))
        const hint = hints.get(label(s))
        return (
          <label key={s.id} className="field">
            <span>
              {speakerDisplayName(s.etiqueta)}
              {t ? ` · habló ${formatLength(t.ms)} (${Math.round(t.share * 100)}%)` : ''}
            </span>
            <input
              value={names[s.id] ?? ''}
              placeholder={speakerDisplayName(s.etiqueta)}
              maxLength={80}
              data-autofocus={focus?.id === s.id || undefined}
              onChange={(e) => setNames((n) => ({ ...n, [s.id]: e.target.value }))}
            />
            {hint && !(names[s.id] ?? '').trim() && (
              <button
                type="button"
                className="chip"
                style={{ alignSelf: 'flex-start' }}
                title={`Alguien dijo «${hint.nombre}» justo antes de que hablara (${hint.veces} ${hint.veces === 1 ? 'vez' : 'veces'})`}
                onClick={(e) => {
                  e.preventDefault()
                  setNames((n) => ({ ...n, [s.id]: hint.nombre }))
                }}
              >
                ¿Es {hint.nombre}?
              </button>
            )}
          </label>
        )
      })}
      {detail.speakers.length > 1 && (
        <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
          <span>¿Una misma persona aparece como dos? Fusiónalas</span>
          <div className="row wrap">
            <select
              aria-label="Hablante a fusionar"
              value={mergeFrom}
              onChange={(e) => setMergeFrom(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Elegir…</option>
              {detail.speakers.map((s) => (
                <option key={s.id} value={s.id}>
                  {label(s)}
                </option>
              ))}
            </select>
            <span className="faint">es la misma persona que</span>
            <select
              aria-label="Fusionar en"
              value={mergeInto}
              onChange={(e) => setMergeInto(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Elegir…</option>
              {detail.speakers
                .filter((s) => s.id !== mergeFrom)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {label(s)}
                  </option>
                ))}
            </select>
          </div>
        </fieldset>
      )}
    </Modal>
  )
}
